import { Box } from "@mui/material";
import { ReactNode, Ref } from "react";

import { VIEWPORT_COMPACT_MAX_MEDIA } from "config";
import CustomScrollbars from "components/CustomScrollbars";

/**
 * Shared header-cell styling for Bots Management tables. Opaque background so a
 * `stickyHeader` row never shows body rows bleeding through while scrolling.
 */
export const BOTS_HEADER_CELL_SX = {
  fontWeight: 700,
  textAlign: "center" as const,
  verticalAlign: "middle" as const,
  borderBottom: 2,
  borderColor: "divider",
  whiteSpace: "nowrap" as const,
  bgcolor: (theme: { palette: { mode: string } }) =>
    theme.palette.mode === "dark" ? "background.paper" : "grey.50",
  // A laptop pays 16px of gutter per column for nothing; halving it buys back
  // ~8px a column across every table here at once.
  [VIEWPORT_COMPACT_MAX_MEDIA]: { px: 1 },
};

/** Body-cell default: centered, vertically aligned, single-line (ellipsis via `noWrap` Typography). */
export const BOTS_BODY_CELL_SX = {
  textAlign: "center" as const,
  verticalAlign: "middle" as const,
  [VIEWPORT_COMPACT_MAX_MEDIA]: { px: 1 },
};

interface BotsTableScrollProps {
  children: ReactNode;
  /**
   * Fill the parent flex area and scroll internally (sticky header stays put).
   * Use inside height-constrained panels. When false the wrapper sizes to its
   * content and only scrolls horizontally.
   */
  fill?: boolean;
  /** Draw the rounded outlined frame around the table (replaces `Paper variant="outlined"`). */
  bordered?: boolean;
  sx?: object;
  /** The outer box, for measuring the width the table really has. */
  measureRef?: Ref<HTMLDivElement>;
}

/**
 * Unified scroller for every Bots Management table.
 *
 * The inner `<Table>` keeps a fixed `minWidth`, so the columns stay rigid and
 * the area scrolls instead of squeezing / overlapping. That policy is tiered,
 * not absolute: below the card breakpoint the entity tables stop being tables
 * altogether (see `BotsRecordTable`), and on a laptop they switch to a narrower
 * set of column widths - horizontal scroll is the last resort, not the plan.
 * Vertical scrolling goes through CustomScrollbars; the horizontal bar stays
 * native, because a table wider than its panel needs one to be dragged by.
 */
export default function BotsTableScroll({
  children,
  fill = false,
  bordered = true,
  sx,
  measureRef,
}: BotsTableScrollProps) {
  return (
    <Box
      ref={measureRef}
      data-testid="bots-table-scroll"
      sx={{
        width: "100%",
        minWidth: 0,
        display: "flex",
        overflow: "hidden",
        ...(fill ? { flex: 1, minHeight: 0 } : {}),
        ...(bordered
          ? {
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }
          : {}),
        ...sx,
      }}
    >
      <CustomScrollbars hideNative="vertical" trackGutter={fill}>
        {children}
      </CustomScrollbars>
    </Box>
  );
}
